/**
 * The Ship gadgets' plain logic (components/home/gadgets: Release, Environments, Configuration health). Pure: the app's
 * versions, the changes since a time, the activity timeline and the app configuration go in.
 */
import { shortServiceName } from '../gitRepoSource';
import { serviceOfPath } from './build';

/** The newest version saved by a Publish (`listVersions`, newest first), or null when the app was never published. */
export const lastPublish = (versions) => (versions?.versions || [])
  .filter((v) => v.trigger === 'publish' && v.createdAt)
  .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))[0] || null;

/**
 * What changed since a time (`GET /collab/changes?since=`): `{ commits, files, services, people }`, `services` the
 * service names touched (sorted), `people` who made them (the credited person's name).
 */
export function changesSummary(changes = []) {
  const files = new Set();
  const services = new Set();
  const people = new Set();
  (changes || []).forEach((change) => {
    (change.files || []).forEach((file) => {
      files.add(file.path);
      const service = serviceOfPath(file.path);
      if (service) { services.add(service); }
    });
    if (change.author?.name) { people.add(change.author.name); }
  });
  return { commits: (changes || []).length, files: files.size, services: [...services].sort(), people: [...people].sort() };
}

/**
 * What runs where, from the deploys Console Next reported in the project's History (activity `deploy` events with an
 * `environment`, newest first): the newest per environment, `[{ environment, version, outcome, url, link, at, summary }]`,
 * in the order the environments were last deployed.
 */
export function environmentsOf(events = []) {
  const byEnv = new Map();
  [...(events || [])]
    .filter((e) => e && e.type === 'deploy' && e.at)
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
    .forEach((e) => {
      const environment = e.environment || 'unknown';
      if (byEnv.has(environment)) { return; }
      byEnv.set(environment, { environment, version: e.version || null, outcome: e.outcome || null, url: e.url || null, link: e.link || null, at: e.at, summary: e.summary || null });
    });
  return [...byEnv.values()];
}

/**
 * Configuration health from the app configuration (`getAppConfig` and each service's `getServiceConfig`):
 * - `secrets`: the secrets a service declares and nobody set, `[{ service, name }]`;
 * - `partialVars`: a variable at least two services set but not every service that has variables, `[{ name, in, missing }]`
 *   (a variable one service alone has is normal and not listed);
 * - `paused`: the scheduled jobs switched off, `[{ service, name }]`;
 * - `restart`: the services that need a restart to apply a change.
 */
export function configHealth({ overview = null, details = {} } = {}) {
  const services = overview?.services || [];
  const secrets = services.flatMap((s) => (s.secretsMissing || []).map((name) => ({ service: s.name, name })));
  const withVars = Object.entries(details).filter(([, d]) => (d?.vars || []).length > 0);
  const varServices = new Map();
  withVars.forEach(([service, d]) => (d.vars || []).forEach((v) => {
    if (!v?.name) { return; }
    if (!varServices.has(v.name)) { varServices.set(v.name, new Set()); }
    varServices.get(v.name).add(service);
  }));
  const all = withVars.map(([service]) => service);
  const partialVars = [...varServices.entries()]
    .filter(([, set]) => set.size >= 2 && set.size < all.length)
    .map(([name, set]) => ({ name, in: [...set].sort(), missing: all.filter((s) => !set.has(s)).sort() }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const paused = Object.entries(details).flatMap(([service, d]) => (d?.jobs || []).filter((j) => j && j.enabled === false).map((j) => ({ service, name: j.name })));
  const restart = services.filter((s) => s.needsRestart).map((s) => shortServiceName(s.name));
  return { secrets, partialVars, paused, restart };
}
